import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { TOKEN_STORAGE_KEY } from "@/lib/constants";
import { decodeJwt, isTokenExpired, type JwtPayload } from "@/auth/decodeJwt";
import { authApi } from "@/api/auth";
import { getApiErrorMessage } from "@/api/errors";

// ── Types ───────────────────────────────────────────────────────────────

export type AuthState =
  | "authenticated"
  | "locked"
  | "signed-out"
  | "forgot-password";

export interface CurrentUser {
  fullName: string;
  email: string;
  initials: string;
  businessId: string;
  roles: string[];
}

interface AuthContextValue {
  authState: AuthState;
  previousAuthState: AuthState;
  token: string | null;
  currentUser: CurrentUser | null;
  /** Async login via platform API. Returns null on success, error message on failure. */
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => void;
  lockApp: () => void;
  /** Unlock by re-authenticating against the same email. Returns null on success, error message on failure. */
  unlockApp: (password: string) => Promise<string | null>;
  goToForgotPassword: () => void;
  goBackFromForgot: () => void;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function userFromPayload(payload: JwtPayload): CurrentUser {
  const first = (payload.firstName as string) ?? "";
  const last = (payload.lastName as string) ?? "";
  const fullName = [first, last].filter(Boolean).join(" ") || "Staff";
  const initials =
    (first[0] ?? "").toUpperCase() + (last[0] ?? "").toUpperCase() || "S";
  return {
    fullName,
    email: payload.email ?? payload.sub ?? "",
    initials,
    businessId: String(payload.businessId ?? ""),
    roles: payload.roles ?? [],
  };
}

/**
 * Check the URL for an incoming `?token=` param (set by the admin app when
 * switching to staff view). If present, persist it to localStorage and strip
 * it from the URL so it doesn't leak into the address bar or browser history.
 */
function consumeTokenFromUrl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get("token");
  if (!urlToken) return;

  const payload = decodeJwt(urlToken);
  if (!isTokenExpired(payload)) {
    localStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
  }

  // Clean the URL — remove ?token= without a full page reload.
  params.delete("token");
  const clean = params.toString();
  const newUrl = window.location.pathname + (clean ? `?${clean}` : "") + window.location.hash;
  window.history.replaceState(null, "", newUrl);
}

function loadStoredToken(): { token: string; user: CurrentUser } | null {
  if (typeof window === "undefined") return null;
  // Consume any incoming token from the admin app before reading localStorage.
  consumeTokenFromUrl();
  const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!stored) return null;
  const payload = decodeJwt(stored);
  if (isTokenExpired(payload)) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return null;
  }
  return { token: stored, user: userFromPayload(payload!) };
}

// ── Provider ────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initial = loadStoredToken();
  const [token, setToken] = useState<string | null>(initial?.token ?? null);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(
    initial?.user ?? null,
  );
  const [authState, setAuthState] = useState<AuthState>(
    initial ? "authenticated" : "signed-out",
  );
  const [previousAuthState, setPreviousAuthState] = useState<AuthState>(
    initial ? "authenticated" : "signed-out",
  );

  // Re-validate token when it changes (e.g. another tab cleared it)
  useEffect(() => {
    if (token) {
      const payload = decodeJwt(token);
      if (isTokenExpired(payload)) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken(null);
        setCurrentUser(null);
        setAuthState("signed-out");
      } else {
        setCurrentUser(userFromPayload(payload!));
      }
    } else {
      setCurrentUser(null);
    }
  }, [token]);

  // ── Actions ─────────────────────────────────────────────────────────

  const signIn = useCallback(
    async (email: string, password: string): Promise<string | null> => {
      try {
        const { accessToken } = await authApi.login({ email, password });
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        setToken(accessToken);
        setAuthState("authenticated");
        return null;
      } catch (err) {
        return getApiErrorMessage(err, "Login failed. Please try again.");
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
    setCurrentUser(null);
    setAuthState("signed-out");
  }, []);

  const lockApp = useCallback(() => {
    // Lock is a UI overlay — the token stays valid so the session
    // survives a screen lock on a shared salon tablet.
    setAuthState("locked");
  }, []);

  const unlockApp = useCallback(
    async (password: string): Promise<string | null> => {
      if (!currentUser?.email) {
        setAuthState("signed-out");
        return "Session expired. Please sign in again.";
      }
      // Re-authenticate with the same email to verify identity.
      return signIn(currentUser.email, password);
    },
    [currentUser?.email, signIn],
  );

  const goToForgotPassword = useCallback(() => {
    setPreviousAuthState(authState);
    setAuthState("forgot-password");
  }, [authState]);

  const goBackFromForgot = useCallback(() => {
    setAuthState(
      previousAuthState === "forgot-password"
        ? "signed-out"
        : previousAuthState,
    );
  }, [previousAuthState]);

  // ── Value ─────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      authState,
      previousAuthState,
      token,
      currentUser,
      signIn,
      signOut,
      lockApp,
      unlockApp,
      goToForgotPassword,
      goBackFromForgot,
    }),
    [
      authState,
      previousAuthState,
      token,
      currentUser,
      signIn,
      signOut,
      lockApp,
      unlockApp,
      goToForgotPassword,
      goBackFromForgot,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
